import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  #gridToggleAnimating = false;

  /**
   * SJ added: toggles the desktop grid between the collapsed (limited) and
   * fully expanded states via the "See more / See less" button. The overflow
   * tiles slide open/closed (height + fade) instead of jumping in and out.
   */
  toggleGridLimit() {
    if (this.#gridToggleAnimating) return;

    const button = this.refs.seeMoreButton;
    const items = Array.from(this.querySelectorAll('.media-gallery__grid .media-gallery__grid-item--overflow'));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timing = { duration: 600, easing: 'cubic-bezier(0.33, 0, 0.2, 1)' };
    const isCollapsed = this.classList.contains('media-gallery--grid-collapsed');

    if (isCollapsed) {
      this.classList.remove('media-gallery--grid-collapsed');
      button?.setAttribute('aria-expanded', 'true');

      if (reduceMotion) return;

      for (const item of items) {
        item.animate(
          [
            { height: '0px', opacity: 0, overflow: 'hidden' },
            { height: `${item.offsetHeight}px`, opacity: 1, overflow: 'hidden' },
          ],
          timing
        );
      }
    } else {
      button?.setAttribute('aria-expanded', 'false');

      const finishCollapse = (animations) => {
        this.classList.add('media-gallery--grid-collapsed');
        animations.forEach((animation) => animation.cancel());
        this.#gridToggleAnimating = false;
      };

      if (reduceMotion || items.length === 0) {
        finishCollapse([]);
        return;
      }

      this.#gridToggleAnimating = true;
      let finished = 0;
      const animations = items.map((item) =>
        item.animate(
          [
            { height: `${item.offsetHeight}px`, opacity: 1, overflow: 'hidden' },
            { height: '0px', opacity: 0, overflow: 'hidden' },
          ],
          { ...timing, fill: 'forwards' }
        )
      );
      for (const animation of animations) {
        animation.onfinish = () => {
          finished += 1;
          if (finished === animations.length) finishCollapse(animations);
        };
      }
    }
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
